"use client";

import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function WhatWeHaulAway() {
  const header = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const tiers = [{
    index: "[ 01 // RESIDENTIAL ]",
    name: "Home Cleanouts",
    coords: "\n",
    description: "Full-service removal of household clutter, old furniture, and appliances. We handle the heavy lifting from your garage, attic, or backyard so you don't have to.",
    inclusions: ["Furniture & Appliance Removal", "Garage & Attic Purging", "Estate Cleanout Services", "Eco-Friendly Disposal"],
    recommended: false
  }, {
    index: "[ 02 // INDUSTRIAL ]",
    name: "Construction Debris",
    coords: "\n",
    description: "Heavy-duty hauling for job sites and renovations. We clear concrete, wood, metal, and roofing materials with industrial-grade efficiency and speed.",
    inclusions: ["Concrete & Brick Hauling", "Renovation Waste Removal", "Site Prep & Clearing", "Same-Day Priority Pickup"],
    recommended: true
  }, {
    index: "[ 03 // COMMERCIAL ]",
    name: "Business Logistics",
    coords: "JOE & GIO HAUL IT ALL",
    description: "Professional junk removal for offices, retail spaces, and property managers. Reliable scheduling and bulk removal to keep your business operations running smooth.",
    inclusions: ["Office Furniture Disposal", "Retail Fixture Removal", "Property Manager Support", "Bulk Waste Contracts"],
    recommended: false
  }];
  return <section data-section-id="2043" id="what-we-haul-away" className="bg-background text-foreground py-28 md:py-36 font-default">
      <div className="max-w-[1480px] mx-auto px-8 md:px-12 lg:px-16">
        <div ref={header.ref} className={`relative grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 mb-16 md:mb-20 transition-all duration-700 ease-out ${header.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div aria-hidden="true" className="pointer-events-none absolute -top-8 left-0 lg:-top-16 lg:-left-4 font-default font-black select-none leading-none" style={{
          fontSize: "clamp(8rem, 22vw, 22rem)",
          color: "transparent",
          WebkitTextStroke: "1px var(--primary)",
          opacity: 0.05,
          letterSpacing: "-0.05em"
        }}>
            HAUL
          </div>

          <div className="lg:col-span-8 relative z-10">
            <div className="flex items-center gap-3 mb-8">
              <span className="font-mono uppercase font-semibold" style={{
              fontSize: "clamp(0.6875rem, 0.8vw, 0.8125rem)",
              letterSpacing: "0.2em",
              color: "var(--primary)"
            }}>
                [ 01 // THE MISSION / SERVICE TIERS ]
              </span>
            </div>

            <div className="flex items-center gap-0 mb-8">
              <div className="h-1 flex-1 bg-[color:var(--secondary)] opacity-20" />
              <div className="h-3 w-3 bg-[color:var(--primary)]" />
            </div>

            <h2 className="font-default font-black uppercase" style={{
            fontSize: "clamp(2.5rem, 5.5vw, 5.25rem)",
            letterSpacing: "-0.04em",
            lineHeight: 0.9
          }}>
              Heavy-duty{" "}
              <span className="text-[color:var(--primary)]" style={{
              letterSpacing: "-0.02em"
            }}>
                standards
              </span>
              .
            </h2>
          </div>

          <div className="lg:col-span-4 lg:pt-4 relative z-10 flex flex-col justify-between">
            <p className="font-default" style={{
            fontSize: "clamp(1.0625rem, 1.25vw, 1.25rem)",
            lineHeight: 1.55,
            color: "var(--muted-foreground)"
          }}>
              Joe & Gio Haul It All provides high-performance logistics for your toughest cleanups. Select your service tier.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {tiers.map((tier, i) => <TierCard key={i} tier={tier} index={i} />)}
        </div>
      </div>
    </section>;
}
function TierCard({
  tier,
  index
}: {
  tier: {
    index: string;
    name: string;
    coords: string;
    description: string;
    inclusions: string[];
    recommended: boolean;
  };
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <div ref={ref} data-index={index} className="group relative bg-[color:var(--card)] p-10 transition-all duration-300 ease-out" style={{
    border: "1px solid var(--border)",
    boxShadow: "var(--shadow-md)",
    transitionDelay: `${index * 80}ms`,
    opacity: isVisible ? 1 : 0,
    transform: isVisible ? "translateY(0)" : "translateY(24px)"
  }} onMouseEnter={e => {
    e.currentTarget.style.border = "1px solid var(--primary)";
    e.currentTarget.style.transform = "translateY(-8px)";
    e.currentTarget.style.boxShadow = "var(--shadow-xl)";
  }} onMouseLeave={e => {
    e.currentTarget.style.border = "1px solid var(--border)";
    e.currentTarget.style.transform = "translateY(0)";
    e.currentTarget.style.boxShadow = "var(--shadow-md)";
  }}>
      <CornerTick position="tl" />
      <CornerTick position="tr" />
      <CornerTick position="bl" />
      <CornerTick position="br" />

      <div className="flex items-center justify-between mb-6">
        <span className="font-mono uppercase font-bold" style={{
        fontSize: "0.75rem",
        letterSpacing: "0.16em",
        color: "var(--muted-foreground)"
      }}>
          {tier.index}
        </span>
        {tier.recommended && <div className="flex items-center gap-2 bg-[color:var(--primary)] px-2 py-1">
            <span className="font-mono uppercase font-black" style={{
          fontSize: "0.625rem",
          letterSpacing: "0.1em",
          color: "var(--primary-foreground)"
        }}>
              Most Popular
            </span>
          </div>}
      </div>

      <h3 className="font-default font-bold mb-2 uppercase" style={{
      fontSize: "clamp(1.5rem, 2vw, 2rem)",
      letterSpacing: "-0.02em",
      lineHeight: 1.05
    }}>
        {tier.name}
      </h3>

      <div className="font-mono mb-6 font-semibold" style={{
      fontSize: "0.75rem",
      letterSpacing: "0.08em",
      color: "var(--primary)"
    }}>
        {tier.coords}
      </div>

      <div className="flex items-center gap-0 mb-6">
        <div className="h-1 flex-1 bg-[color:var(--border)]" />
        <div className="h-2 w-2 bg-[color:var(--primary)]" />
      </div>

      <p className="font-default mb-8" style={{
      fontSize: "1rem",
      lineHeight: 1.65,
      color: "var(--muted-foreground)"
    }}>
        {tier.description}
      </p>

      <ul className="space-y-3 mb-10">
        {tier.inclusions.map((inc, j) => <li data-index={j} key={j} className="flex items-start gap-3 font-mono uppercase font-medium" style={{
        fontSize: "0.6875rem",
        letterSpacing: "0.14em",
        lineHeight: 1.4,
        color: "var(--foreground)"
      }}>
            <span className="mt-1 h-1.5 w-1.5 bg-[color:var(--primary)] shrink-0" aria-hidden="true" />
            <span>{inc}</span>
          </li>)}
      </ul>

      <Button asChild variant="link" className="p-0 h-auto font-mono uppercase no-underline hover:no-underline">
        <Link to="/HomePage" className="group/cta inline-flex items-center gap-2 font-bold" style={{
        fontSize: "0.75rem",
        letterSpacing: "0.16em",
        color: "var(--foreground)"
      }}>
          <span className="transition-colors duration-200 group-hover/cta:text-[color:var(--primary)]">
            Book Haul
          </span>
          <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover/cta:translate-x-1 text-[color:var(--primary)]" strokeWidth={2.5} />
        </Link>
      </Button>
    </div>;
}
function CornerTick({
  position
}: {
  position: "tl" | "tr" | "bl" | "br";
}) {
  const base = "absolute pointer-events-none transition-all duration-300";
  const size = "w-2 h-2 group-hover:w-3 group-hover:h-3";
  const offsets: Record<string, string> = {
    tl: "top-0 left-0",
    tr: "top-0 right-0",
    bl: "bottom-0 left-0",
    br: "bottom-0 right-0"
  };
  const borders: Record<string, string> = {
    tl: "border-t-2 border-l-2",
    tr: "border-t-2 border-r-2",
    bl: "border-b-2 border-l-2",
    br: "border-b-2 border-r-2"
  };
  return <span aria-hidden="true" className={`${base} ${size} ${offsets[position]} ${borders[position]} border-[color:var(--primary)]`} />;
}